/**
 * salesFigures.ts — ONE read of /v1/sales/reports + /v1/sales/stats, shared by
 * the Pipeline board's header and the sidebar's Sales counts (register row 84,
 * RULED option 4 by Klemen 2026-10-01; build log §38.20).
 *
 * The defect it closes: the board header and the sidebar count each made
 * their own read with no refetch handle, so both went stale after a close or
 * a create — the lanes read 8 open deals under a header still reading 9.
 *
 * - ONE READ, TWO HALVES. A read fetches both routes together; each half
 *   lands on its own, so a failed /stats never costs the board its /reports
 *   figures (the board's "shorter summary row" rule, unchanged).
 * - loadSalesFigures() — NAVIGATION (a Sales page change, the board mounting):
 *   joins a read already in flight, else starts one. This is what makes the
 *   shell's read and the board's read the same read.
 * - refreshSalesFigures() — AFTER A WRITE that changes the figures (a close,
 *   a create): ALWAYS a new read, and the newest read started is the only one
 *   whose result is applied — a read that began before the write can never
 *   overwrite the one that began after it. An open-to-open stage move changes
 *   none of these figures and never calls this.
 * - SILENT: there is no loading state. The last figures stay on screen until
 *   the new ones land.
 * - BOUNDS, stated: between the write and the landing the header / count can
 *   disagree with the lanes for the length of one round trip; and a FAILED
 *   re-read leaves the old figures standing, half by half, with nothing on
 *   screen saying so. Only a FIRST read's failure renders nothing (the slot
 *   rule: no number, no chip).
 * - TENANT: figures are stamped with the tenant they were read for and read
 *   as absent under any other, so a module-level store can never show one
 *   tenant's numbers on another's page.
 *
 * Refused, with reasons (the register row): deriving the header from the
 * lanes, applying a deal's delta client-side, aggregates on write responses.
 */
import { useSyncExternalStore } from 'react'
import { getTenant } from '../../api/client'
import type { SalesReports, SalesStats } from '../../api/types'
import { fetchSalesReports, fetchSalesStats } from './api'

export interface SalesFigures { reports: SalesReports | null; stats: SalesStats | null }

const EMPTY: SalesFigures = { reports: null, stats: null }

let figures: SalesFigures = EMPTY
let figuresTenant: string | null = null
let newest = 0
// H-1a: how many post-write refreshes have been asked for — a signal, not a
// figure (useSalesRefreshes below).
let refreshes = 0
let inFlight: Promise<void> | null = null
const listeners = new Set<() => void>()

const emit = () => { for (const l of listeners) l() }

function start(): Promise<void> {
  const mine = ++newest
  const tenant = getTenant()
  // .then(fn) rather than fn(): a synchronous throw is a failed read, never an
  // exception in the caller — the write that asked for this already succeeded.
  const read: Promise<void> = Promise.allSettled([
    Promise.resolve().then(() => fetchSalesReports()),
    Promise.resolve().then(() => fetchSalesStats()),
  ]).then(([r, s]) => {
    if (inFlight === read) inFlight = null
    if (mine !== newest || tenant !== getTenant()) return // superseded, or the tenant moved on
    const held = figuresTenant === tenant ? figures : EMPTY
    const next: SalesFigures = {
      reports: r.status === 'fulfilled' ? r.value : held.reports,
      stats: s.status === 'fulfilled' ? s.value : held.stats,
    }
    figuresTenant = tenant
    if (next.reports === figures.reports && next.stats === figures.stats) return
    figures = next
    emit()
  })
  inFlight = read
  return read
}

/** Navigation: join the read in flight, or start one. */
export function loadSalesFigures(): Promise<void> {
  return inFlight ?? start()
}

/** After a successful close or create: a new read that supersedes any other.
 *  H-1a: it also COUNTS — useSalesRefreshes() lets Sales Home re-read its own
 *  summary after a write it did not make (a call logged from the sheet, a
 *  close on the board) on the SAME signal the header and the sidebar follow. */
export function refreshSalesFigures(): Promise<void> {
  refreshes++
  emit()
  return start()
}

/** Leaving Sales (or the shell unmounting): drop the figures and orphan any
 *  read in flight, so re-entering Sales starts from nothing, as it always has. */
export function resetSalesFigures(): void {
  newest++
  inFlight = null
  figuresTenant = null
  if (figures === EMPTY) return
  figures = EMPTY
  emit()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

const snapshot = (): SalesFigures => (figuresTenant === getTenant() ? figures : EMPTY)

/** The last figures that landed — null halves until a first read does. */
export function useSalesFigures(): SalesFigures {
  return useSyncExternalStore(subscribe, snapshot)
}

const refreshSnapshot = (): number => refreshes

/** The count of post-write refreshes asked for since the module loaded — a
 *  dependency for a page that must re-read its OWN routes on that signal
 *  (Sales Home's summary, H-1a). A navigation load does not move it. Never
 *  reset and never tenant-stamped: it is a counter and carries no data. */
export function useSalesRefreshes(): number {
  return useSyncExternalStore(subscribe, refreshSnapshot)
}
